import _ from "lodash";

/**
 * Initializes WPD Dataset.
 */
function init(wpd, dataEntryVM) {
	if (dataEntryVM.pointGroupOverhaul) {
		// NOTE consider overriding the entire dataset class when deprecating
		// pointGroupOverhaul

		// overriding the constructor to omit specific keys
		const OriginalDataset = wpd.Dataset;
		wpd.Dataset = function (...args) {
			// call the original constructor
			const instance = new OriginalDataset(...args);

			// clean up unwanted properties
			delete instance._dim;
			delete instance._connections;
			delete instance._groupNames;
			delete instance._tuples;

			return instance;
		};

		// copy the prototype and static properties
		wpd.Dataset.prototype = OriginalDataset.prototype;
		wpd.Dataset.static = OriginalDataset.static;

		// ensure the constructor property points to the new constructor
		wpd.Dataset.prototype.constructor = wpd.Dataset;

		/**
		 * Checks if the dataset has point groups for the given axis direction.
		 * @param {string} axisDirection - Optional axis direction to check for point groups
		 * @returns {boolean} - True if the dataset has point groups for the given axis direction, false otherwise
		 */
		wpd.Dataset.prototype.hasPointGroups = function (axisDirection) {
			const metadata = this.getMetadata();

			if (metadata.pointGroups) {
				if (axisDirection) {
					return !!metadata.pointGroups?.filter(
						(pointGroup) => pointGroup.axis === axisDirection,
					)?.length;
				}
				return !!metadata.pointGroups?.length;
			}
			return false;
		};

		/**
		 * Gets the point groups from the dataset.
		 * @returns {object} - Point groups for the dataset
		 */
		wpd.Dataset.prototype.getPointGroups = function () {
			const metadata = this.getMetadata();
			return metadata.pointGroups || [];
		};

		/**
		 * Sets the point groups for the dataset.
		 * @param {object} mainTagUuids - Main tag uuids for the point groups
		 */
		wpd.Dataset.prototype.setPointGroups = function (mainTagUuids) {
			const plotData = wpd.appData.getPlotData();
			const axis = plotData.getAxesForDataset(this);
			const axisMetadata = axis.getMetadata();

			// if the axis metadata does not have point groups and there are
			// main tag uuids, throw an error
			if (!axisMetadata.pointGroups && !_.isEmpty(mainTagUuids)) {
				console.error("axis metadata not found while setting point groups");

				return;
			}

			// get the dataset metadata
			const datasetMetadata = this.getMetadata();

			// if there are no main tag uuids, clear the point groups
			if (_.isEmpty(mainTagUuids)) {
				delete datasetMetadata.pointGroups;

				this.setMetadata(datasetMetadata);
			} else {
				// create center point groups
				const centerPointGroups = [
					...(axisMetadata.pointGroups.x && mainTagUuids.x
						? [
								{
									tagUuid: mainTagUuids.x,
									axis: "x",
									center: true,
								},
						  ]
						: []),
					...(axisMetadata.pointGroups.y && mainTagUuids.y
						? [
								{
									tagUuid: mainTagUuids.y,
									axis: "y",
									center: true,
								},
						  ]
						: []),
				];

				const pointGroups = [
					...(axisMetadata.pointGroups.x?.map((tag) => ({
						tagUuid: tag.uuid,
						axis: "x",
					})) || []),
					...(axisMetadata.pointGroups.y?.map((tag) => ({
						tagUuid: tag.uuid,
						axis: "y",
					})) || []),
				];

				// add center point groups to the point groups array
				// keep n > 1 center point groups at the end to simplify display logic
				centerPointGroups.forEach((centerPointGroup, index) => {
					if (index === 0) {
						pointGroups.unshift(centerPointGroup);
					} else {
						pointGroups.push(centerPointGroup);
					}
				});

				// set point groups using provided central tendie tags and
				// point group settings from the axis metadata
				this.setMetadata(_.assign(datasetMetadata, { pointGroups }));
			}
		};
	}

	if (dataEntryVM.viewEditOverlappingDataPoints) {
		const OVERLAP_THRESHOLD = 1;

		/**
		 * Finds all pixels that overlap with the given pixel.
		 * @param {number} index - Index of the pixel to find overlapping pixels for
		 * @returns {array} - Array of overlapping pixels
		 */
		wpd.Dataset.prototype.findOverlappingPixels = function (index) {
			if (index < 0) {
				return [];
			}

			const pixel = this.getPixel(index);

			if (!pixel) {
				console.error(`pixel not found for index ${index}`);
				return [];
			}

			// get all datasets
			const allDatasets = wpd.appData.getPlotData().getDatasets();

			const activeDataset = wpd.tree.getActiveDataset();

			// if a dataset is currently selected, only check for overlapping pixels in that dataset
			// otherwise, filter by the file and page of the current dataset
			const filteredDatasets = activeDataset
				? [activeDataset]
				: allDatasets.filter((dataset) => {
						return (
							dataset.getFileAndPage().fileIndex ===
								this.getFileAndPage().fileIndex &&
							dataset.getFileAndPage().pageNumber ===
								this.getFileAndPage().pageNumber
						);
				  });

			return filteredDatasets.flatMap((dataset) => {
				return dataset
					.getAllPixels()
					.map((pixel, index) => ({ pixel, index }))
					.filter((p) => {
						const dx = p.pixel.x - pixel.x;
						const dy = p.pixel.y - pixel.y;
						const distance = Math.sqrt(dx * dx + dy * dy);
						return distance < OVERLAP_THRESHOLD;
					});
			});
		};
	}

	const originalClearAll = wpd.Dataset.prototype.clearAll;
	// select datasets group item after loading (for first load)
	wpd.Dataset.prototype.clearAll = function () {
		const metadata = this.getMetadata();

		originalClearAll.call(this);

		if (metadata.table && metadata.colIndex) {
			this.setMetadata(metadata);

			wpd.tree.selectDataset(wpd.tree.getActiveDataset().name);
		}
	};

	/**
	 * Gets all tag group UUIDs associated with data points in this dataset.
	 * @returns {array} Array of objects, each with uuid, name, and instanceUuid
	 */
	wpd.Dataset.prototype.getTagGroups = function () {
		return this.getAllPixels().map(
			(dataPoint) => dataPoint?.metadata?.tagGroup,
		);
	};

	wpd.Dataset.prototype.findTagGroup = function (instanceUuid) {
		return this.getTagGroups().find(
			(tagGroup) => tagGroup.instanceUuid === instanceUuid,
		);
	};

	/**
	 * Gets the tag group UUID associated with the given data point.
	 * @param {number} index Index of data point within the dataset
	 * @returns
	 */
	wpd.Dataset.prototype.getTagGroup = function (index) {
		return this.getPixel(index)?.metadata?.tagGroup;
	};

	/**
	 * Associates the given tag group UUID with the given data point index.
	 * @param {number} index Index of data point within the dataset
	 * @param {object} tagGroup Tag group object to associate with data point
	 * @param {string} instanceUuid Tag group instance UUID to associate with
	 * 								data point
	 */
	wpd.Dataset.prototype.setTagGroup = function (index, tagGroup, instanceUuid) {
		const dataPoint = this.getPixel(index);

		if (tagGroup === null || tagGroup === undefined) {
			// completely remove the key if the passed in
			// tag group is null or undefined
			delete dataPoint?.metadata?.tagGroup;
		} else {
			// define the tag group data
			const tagGroupData = Object.assign({}, tagGroup, {
				instanceUuid,
			});

			// get the existing metadata if it exists and add tag group data
			let metadata = dataPoint.metadata || {};
			Object.assign(metadata, {
				tagGroup: tagGroupData,
			});

			// set the metadata for the data point
			this.setMetadataAt(index, metadata);
		}
	};

	/**
	 * Gets all indexes of data points associated with the given data point's
	 * tag group instance uuid within the dataset.
	 * @param {Number}	index	Index of data point with tag group instance uuid
	 * 							to search for
	 * @returns {Array}	Indexes of associated data points, including the given
	 * 					data point index
	 */
	wpd.Dataset.prototype.getTagGroupInstancePixelIndexes = function (index) {
		const allDataPoints = this.getAllPixels();

		// find all data points within the dataset with the same
		// tag group instance
		const instanceUuid = _.get(
			this.getPixel(index),
			"metadata.tagGroup.instanceUuid",
		);

		return _.reduce(
			allDataPoints,
			(accumulator, dataPoint, index) => {
				if (
					_.get(dataPoint, "metadata.tagGroup.instanceUuid") === instanceUuid
				) {
					accumulator.push(index);
				}

				return accumulator;
			},
			[],
		);
	};

	/**
	 * Sets all tuples for the dataset.
	 * @deprecated - do not use, will be removed in future versions
	 * @param {array} tuples - array of tuples
	 */
	wpd.Dataset.prototype.setAllTuples = function (tuples) {
		this._tuples = tuples;
	};

	/**
	 * Gets an array of tag objects of the given data point.
	 * @param {*} index - Index of the data point
	 * @returns {array} - Tags of the data point
	 */
	wpd.Dataset.prototype.getTagsForDataPoint = function (dataPoint) {
		const metadata = dataPoint?.metadata;

		if (metadata) {
			return Object.keys(metadata)
				.filter((key) => !isNaN(key))
				.map((key) => metadata[key].tag);
		}
	};

	/**
	 * Finds the nearest pixel by given axis direction and position.
	 * @param {string} direction - Axis direction (x or y)
	 * @param {number} position - Current position (x- or y-value)
	 * @param {number} threshold - Threshold for distance, default 50
	 * @param {array} matchTags - Array of tags to match (AND between tags)
	 * @returns {number} - Index of the nearest pixel
	 */
	wpd.Dataset.prototype.findNearestPixelByAxisDirection = function (
		direction,
		position,
		threshold = 50,
		matchTags = [],
	) {
		let minDist = 0;
		let minIndex = -1;

		this._dataPoints.forEach((dataPoint, index) => {
			// skip data points that do not match all of the match tags
			if (matchTags.length) {
				const tags = this.getTagsForDataPoint(dataPoint);

				if (
					!tags.length ||
					!matchTags.every((matchTag) =>
						tags.some((tag) => tag.uuid === matchTag.uuid),
					)
				) {
					return;
				}
			}

			// calculate the distance
			const dist = Math.abs(position - dataPoint[direction]);

			// track index of data point with smallest distance
			if (
				(minIndex < 0 && dist <= threshold) ||
				(minIndex >= 0 && dist < minDist)
			) {
				minIndex = index;
				minDist = dist;
			}
		});

		return this.getPixel(minIndex);
	};

	/**
	 * Gets the file index and page number associated with the dataset.
	 * @returns {object} - Object with file index and page number
	 */
	wpd.Dataset.prototype.getFileAndPage = function () {
		const fileManager = wpd.appData.getFileManager();
		const datasetFileMap = fileManager.getDatasetNameMap();
		const datasetFileIndex = datasetFileMap[this.name];

		// switch page
		if (wpd.appData.isMultipage()) {
			const pageManager = wpd.appData.getPageManager();
			const datasetPageMap = pageManager.getDatasetNameMap();
			const datasetPageNumber = datasetPageMap[this.name];
			return {
				fileIndex: datasetFileIndex,
				pageNumber: datasetPageNumber,
			};
		}

		return {
			fileIndex: datasetFileIndex,
		};
	};
}

export { init };
